import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { TABLES } from "../../../../../Apis/Tables";
import { CustomCheckbox } from "../../../../../components/CustomCheckbox";
import CustomTd from "../../../../../components/table/CustomTd";
import CustomTh from "../../../../../components/table/CustomTh";
import TableFunction from "../../../../../components/table/TableFunction";
import { CommentsTitle, EmailSubsectionChip, PhoneSubsectionChip } from "../../../../../components/buttons/IconButtons";
import { EVENT_TYPE } from "../Model/Model";
import { MEDIA_OPTIONS } from "../../../Traffic/Manage/Model/Model";
import { STATUS_MODEL } from "../../../../../enums/statusEnum";
import CustomTooltip from "../../../../../components/CustomTooltip";

/**
 * NotificationChannelsTableApp Component
 *
 * Displays the list of event types with their available notification channels (Email, Mobile, App)
 * allowing the user to enable or disable each one using CustomCheckbox components.
 *
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 *
 * @param {Object} props
 * @param {Object} props.item - Event setting object containing `eventSettingMediaOptionDto`
 * @param {Function} props.handleChange - Callback executed when a checkbox changes
 * @returns {Object} - Table configuration and elements for rendering
 */
export const NotificationChannelsTableApp = ({ item, handleChange, disabled }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    /**
     * Store the single item (object) inside results.content to keep table structure consistent.
     */
    const [results, setResults] = useState({
        content: item ? [item] : [],
        totalElements: item?.eventSettingMediaOptionDto.length,
    });

    const [userId] = useState(sessionStorage.getItem("id"));
    const contractorId = sessionStorage.getItem("contractorId");
    const [searchElement, setSearchElement] = useState("");
    const [simpleSearch, setSimpleSearch] = useState(false);
    const [search, setSearch] = useState(false);

    const tableId = "tbl_nct_manage";
    const searchId = "txt_nct_search_manage";
    const option = TABLES.FOODIE_USER_MENU_SELECTION; // TODO: Update if needed

    /** Update results when new item arrives */
    useEffect(() => {
        if (item) {
            setResults({
                content: [item],
                totalElements: 1,
            });
        }
    }, [item]);

    /** Table Header */
    const getThead = () => [
        <CustomTh tableId={tableId} fixedXY={false} left={0} classes="align-middle">
            <CustomTooltip
                text={t("configure_which_channels_to_use_for_each_event_type")}
                classes={"font-color-general-950 ibl-font-w-600 fs-14px text-ellipsis-1-row"}
                showIfTruncated={true}
                placement={"top"}
            />
        </CustomTh>,

        <CustomTh tableId={tableId} classes="text-center align-middle">
            <div className="d-flex flex-column align-items-center justify-content-center gap-2">
                <EmailSubsectionChip width={20} height={20} />
                <span className="fs-12px ibl-font-w-600">{t("email")}</span>
            </div>
        </CustomTh>,

        <CustomTh tableId={tableId} classes="text-center align-middle">
            <div className="d-flex flex-column align-items-center justify-content-center gap-2">
                <PhoneSubsectionChip width={20} height={20} />
                <span className="fs-12px ibl-font-w-600">{t("phone_number")}</span>
            </div>
        </CustomTh>,

        <CustomTh tableId={tableId} classes="text-center align-middle">
            <div className="d-flex flex-column align-items-center justify-content-center gap-2">
                <CommentsTitle width={20} height={20} />
                <span className="fs-12px ibl-font-w-600">{t("app")}</span>
            </div>
        </CustomTh>,
    ];

    /**
     * Build the table body dynamically based on the event types and media options.
     * @returns {{body: Array<Array<React.ReactNode>>, data: Object}}
     */
    const getTbody = () => {
        const content = results?.content || [];
        if (content.length === 0) return { body: [], data: [] };

        const eventSettingMediaOptionDto = content[0]?.eventSettingMediaOptionDto || [];

        // List of all available event types
        const channelNames = Object.values(EVENT_TYPE);

        const body = channelNames.map(({ eventType: eventTypeId, trans }) => {
            /**
             * Check if this media option is active for the current event type.
             * @param {number} mediaOptionId
             * @returns {boolean}
             */
            const isChecked = (mediaOptionId) => {
                return eventSettingMediaOptionDto.some(
                    (opt) =>
                        opt.eventType === eventTypeId &&
                        opt.mediaOption === mediaOptionId &&
                        opt.status?.id === STATUS_MODEL.ACTIVE_COMPANY
                );
            };

            return [
                /** Event name */
                <CustomTd key={`${eventTypeId}-label`} tableId={tableId} classes="align-middle">
                    <span className="ibl-font-w-500 font-color-general-900">{t(trans)}</span>
                </CustomTd>,

                /** Email */
                <CustomTd key={`${eventTypeId}-email`} tableId={tableId} classes="align-middle text-center">
                    <div className="d-flex justify-content-center align-items-center">
                        <CustomCheckbox
                            id={`cb_nct_${eventTypeId}-email`}
                            name={`${eventTypeId}-email`}
                            checked={isChecked(MEDIA_OPTIONS.EMAIL)}
                            onChange={(e) => handleChange(e, null, eventTypeId, MEDIA_OPTIONS.EMAIL)}
                            disabled={disabled}
                        />
                    </div>
                </CustomTd>,

                /** SMS (Mobile) */
                <CustomTd key={`${eventTypeId}-mobile`} tableId={tableId} classes="align-middle text-center">
                    <div className="d-flex justify-content-center align-items-center">
                        <CustomCheckbox
                            id={`cb_nct_${eventTypeId}-mobile`}
                            name={`${eventTypeId}-mobile`}
                            checked={isChecked(MEDIA_OPTIONS.SMS_MESSAGE)}
                            onChange={(e) => handleChange(e, null, eventTypeId, MEDIA_OPTIONS.SMS_MESSAGE)}
                            disabled={disabled}
                        />
                    </div>
                </CustomTd>,

                /** App Notification */
                <CustomTd key={`${eventTypeId}-app`} tableId={tableId} classes="align-middle text-center">
                    <div className="d-flex justify-content-center align-items-center">
                        <CustomCheckbox
                            id={`cb_nct_${eventTypeId}-app`}
                            name={`${eventTypeId}-app`}
                            checked={isChecked(MEDIA_OPTIONS.APP_NOTIFICATION)}
                            onChange={(e) => handleChange(e, null, eventTypeId, MEDIA_OPTIONS.APP_NOTIFICATION)}
                            disabled={disabled}
                        />
                    </div>
                </CustomTd>,
            ];
        });

        return { body, data: results };
    };

    /** Configure TableFunction */
    const { thead, tbody, pagination, page, rowsPerPage } = TableFunction({
        data: results,
        getThead,
        getTbody,
        searchElement,
        fixedPagination: false,
        hasContainer: true,
    });

    return {
        navigate,
        supplierId: userId,
        thead,
        tbody,
        pagination,
        results,
        searchElement,
        setSearchElement,
        option,
        page,
        rowsPerPage,
        setSearch,
        simpleSearch,
        setSimpleSearch,
        contractorId,
        setResults,
        searchId,
        tableId,
    };
};
